import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { followAgent, getFollowersCount, unfollowAgent } from "@/services/AgentService";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";
import toast from "react-hot-toast";

type FollowAction = "follow" | "unfollow";

interface UseAgentFollowOptions {
    followerUserId: string | null;
    followedUserId: string | null;
    fallbackFollowedUserId?: string | null;
    initialIsFollowing?: boolean;
    initialFollowerCount?: number;
    isAuthenticated: boolean;
    onRequireAuth: () => void;
    onMissingIds?: () => void;
    mixpanelLocation?: "Profile" | "Puzzle Page" | "Puzzle Feed" | "Scoreboard";
    nicknameFollowed?: string | null;
    onToggleSuccess?: (action: FollowAction) => void | Promise<void>;
    invalidateQueryKeys?: ReadonlyArray<readonly unknown[]>;
}

interface ToggleFollowOptions {
    onError?: (error?: unknown) => void;
}

export default function useAgentFollow({
    followerUserId,
    followedUserId,
    fallbackFollowedUserId,
    initialIsFollowing,
    initialFollowerCount,
    isAuthenticated,
    onRequireAuth,
    onMissingIds,
    mixpanelLocation,
    nicknameFollowed,
    onToggleSuccess,
    invalidateQueryKeys,
}: UseAgentFollowOptions) {
    const queryClient = useQueryClient();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { userState } = useUser();
    const [isFollowing, setIsFollowing] = React.useState(Boolean(initialIsFollowing));
    const [followerCount, setFollowerCount] = React.useState(
        Number.isFinite(initialFollowerCount) ? Number(initialFollowerCount) : 0,
    );
    const lastHydratedFollowContextRef = React.useRef<string | undefined>(undefined);
    const requestingUserKey = followerUserId ? String(followerUserId) : "anonymous";
    const targetFollowedUserId = followedUserId ?? fallbackFollowedUserId ?? null;
    const targetFollowedUserIdStr = targetFollowedUserId ? String(targetFollowedUserId) : null;
    const applyFollowStateToCommentNode = React.useCallback(
        (node: any, targetUserId: string, nextIsFollowing: boolean): any => {
            if (!node || typeof node !== "object") return node;
            const nodeUserId = node.userId ? String(node.userId) : null;
            const nextNode =
                nodeUserId && nodeUserId === targetUserId ? { ...node, isFollowing: nextIsFollowing } : node;
            if (!Array.isArray(nextNode.replies)) return nextNode;
            const nextReplies = nextNode.replies.map((reply: any) =>
                applyFollowStateToCommentNode(reply, targetUserId, nextIsFollowing),
            );
            return { ...nextNode, replies: nextReplies };
        },
        [],
    );

    React.useEffect(() => {
        // Re-hydrate when either target or viewer changes to avoid stale state
        // when auth context swaps while the same target profile remains visible.
        const nextHydrationContextKey = `${requestingUserKey}:${targetFollowedUserIdStr ?? "none"}`;
        if (lastHydratedFollowContextRef.current === nextHydrationContextKey) {
            return;
        }
        lastHydratedFollowContextRef.current = nextHydrationContextKey;

        if (typeof initialIsFollowing === "boolean") {
            setIsFollowing(initialIsFollowing);
        } else {
            setIsFollowing(false);
        }
        if (typeof initialFollowerCount === "number" && Number.isFinite(initialFollowerCount)) {
            setFollowerCount(initialFollowerCount);
        } else {
            setFollowerCount(0);
        }
    }, [initialFollowerCount, initialIsFollowing, requestingUserKey, targetFollowedUserIdStr]);

    const mutation = useMutation({
        mutationFn: async (action: FollowAction) => {
            const resolvedFollowedUserId = followedUserId ?? fallbackFollowedUserId;
            if (!followerUserId || !resolvedFollowedUserId) {
                throw new Error("Missing follow ids");
            }
            const executeFollowAction = async (targetFollowedUserId: string) => {
                if (action === "unfollow") {
                    return unfollowAgent(followerUserId, targetFollowedUserId);
                }
                return followAgent(followerUserId, targetFollowedUserId);
            };

            try {
                return {
                    result: await executeFollowAction(String(resolvedFollowedUserId)),
                    targetFollowedUserId: String(resolvedFollowedUserId),
                };
            } catch (error) {
                if (
                    fallbackFollowedUserId &&
                    String(fallbackFollowedUserId) !== String(resolvedFollowedUserId)
                ) {
                    return {
                        result: await executeFollowAction(String(fallbackFollowedUserId)),
                        targetFollowedUserId: String(fallbackFollowedUserId),
                    };
                }
                throw error;
            }
        },
        onSuccess: async (mutationData, action) => {
            const mutationFollowedUserId = mutationData?.targetFollowedUserId;
            if (!mutationFollowedUserId) return;
            if (mixpanelLocation) {
                sendMixpanelEvent("Follow-Unfollow", {
                    pageUrl: window.location.href,
                    email_follower: userState?.emailAddress ?? null,
                    nickname_follower: userState?.nickname ?? null,
                    nickname_followed: nicknameFollowed ?? null,
                    type: action === "follow" ? "Follow" : "Unfollow",
                    location: mixpanelLocation,
                });
            }

            queryClient.setQueryData(["agent-followers-count", mutationFollowedUserId], (prev: number | undefined) => {
                    if (typeof prev !== "number" || !Number.isFinite(prev)) return prev;
                    return Math.max(0, prev + (action === "follow" ? 1 : -1));
                });
            const nextIsFollowing = action === "follow";
            const targetUserId = String(mutationFollowedUserId);

            if (targetUserId) {
                // Keep puzzle feed cards in sync.
                queryClient.setQueriesData(
                    {
                        queryKey: ["puzzle-feed"],
                        predicate: (query) =>
                            Array.isArray(query.queryKey) &&
                            query.queryKey[0] === "puzzle-feed" &&
                            (query.queryKey[1] ?? "anonymous") === requestingUserKey,
                    },
                    (prev: any) => {
                    if (!prev?.pages || !Array.isArray(prev.pages)) return prev;
                    const pages = prev.pages.map((page: any) => {
                        const items = page?.data?.data;
                        if (!Array.isArray(items)) return page;
                        const nextItems = items.map((item: any) => {
                            const creatorId = item?.createdBy?.id ? String(item.createdBy.id) : null;
                            if (!creatorId || creatorId !== targetUserId) return item;
                            return {
                                ...item,
                                createdBy: {
                                    ...item.createdBy,
                                    isFollowing: nextIsFollowing,
                                },
                            };
                        });
                        return {
                            ...page,
                            data: {
                                ...page.data,
                                data: nextItems,
                            },
                        };
                    });
                    return { ...prev, pages };
                    },
                );

                // Keep puzzle feed comments previews in sync.
                queryClient.setQueriesData(
                    {
                        queryKey: ["puzzle-feed-comments-preview"],
                        predicate: (query) =>
                            Array.isArray(query.queryKey) &&
                            query.queryKey[0] === "puzzle-feed-comments-preview" &&
                            // Viewer-agnostic (legacy) entries don't include a viewer segment in queryKey[2].
                            // Avoid patching those to prevent follow-state leakage across auth changes.
                            query.queryKey.length >= 3 &&
                            String(query.queryKey[2] ?? "anonymous") === requestingUserKey,
                    },
                    (prev: any) => {
                    if (!prev?.data || !Array.isArray(prev.data)) return prev;
                    return {
                        ...prev,
                        data: prev.data.map((node: any) =>
                            applyFollowStateToCommentNode(node, targetUserId, nextIsFollowing),
                        ),
                    };
                    },
                );

                // Keep scoreboard comments query caches in sync (if present).
                queryClient.setQueriesData(
                    {
                        queryKey: ["playable-property-comments"],
                        predicate: (query) =>
                            Array.isArray(query.queryKey) &&
                            query.queryKey[0] === "playable-property-comments" &&
                            (query.queryKey[2] ?? "anonymous") === requestingUserKey,
                    },
                    (prev: any) => {
                    if (!prev?.data || !Array.isArray(prev.data)) return prev;
                    return {
                        ...prev,
                        data: prev.data.map((node: any) =>
                            applyFollowStateToCommentNode(node, targetUserId, nextIsFollowing),
                        ),
                    };
                    },
                );
            }

            // Keep cached profile headers in sync so navigating to profile reflects
            // follow state immediately without waiting for a network roundtrip.
            queryClient.setQueriesData(
                {
                    queryKey: ["agent-profile-header"],
                    predicate: (query) =>
                        Array.isArray(query.queryKey) &&
                        query.queryKey[0] === "agent-profile-header" &&
                        (query.queryKey[2] ?? "anonymous") === requestingUserKey,
                },
                (prev: any) => {
                if (!prev || typeof prev !== "object") return prev;
                const prevUserId = prev?.userId ? String(prev.userId) : null;
                if (!prevUserId || prevUserId !== targetUserId) {
                    return prev;
                }
                const prevFollowerCount =
                    typeof prev.followerCount === "number" && Number.isFinite(prev.followerCount)
                        ? prev.followerCount
                        : 0;
                return {
                    ...prev,
                    isFollowing: nextIsFollowing,
                    followerCount: Math.max(0, prevFollowerCount + (nextIsFollowing ? 1 : -1)),
                };
                },
            );

            await queryClient.invalidateQueries({ queryKey: ["agent-followers-count", mutationFollowedUserId] });
            await queryClient.invalidateQueries({
                queryKey: ["agent-profile-header"],
                predicate: (query) =>
                    Array.isArray(query.queryKey) &&
                    query.queryKey[0] === "agent-profile-header" &&
                    (query.queryKey[2] ?? "anonymous") === requestingUserKey,
            });
            await queryClient.invalidateQueries({ queryKey: ["agent-profile-stats"] });
            await queryClient.invalidateQueries({
                queryKey: ["puzzle-feed"],
                predicate: (query) =>
                    Array.isArray(query.queryKey) &&
                    query.queryKey[0] === "puzzle-feed" &&
                    (query.queryKey[1] ?? "anonymous") === requestingUserKey,
            });
            await queryClient.invalidateQueries({
                queryKey: ["puzzle-feed-comments-preview"],
                predicate: (query) =>
                    Array.isArray(query.queryKey) &&
                    query.queryKey[0] === "puzzle-feed-comments-preview" &&
                    // Invalidate both:
                    // - viewer-scoped entries for the active requesting user, and
                    // - legacy viewer-agnostic entries (2-part key) so they refetch with the correct viewer context.
                    (query.queryKey.length >= 3
                        ? String(query.queryKey[2] ?? "anonymous") === requestingUserKey
                        : query.queryKey.length === 2),
            });
            await queryClient.invalidateQueries({
                queryKey: ["playable-property-comments"],
                predicate: (query) =>
                    Array.isArray(query.queryKey) &&
                    query.queryKey[0] === "playable-property-comments" &&
                    (query.queryKey[2] ?? "anonymous") === requestingUserKey,
            });
            if (invalidateQueryKeys?.length) {
                await Promise.all(
                    invalidateQueryKeys.map((key) =>
                        queryClient.invalidateQueries({
                            queryKey: key,
                            exact: true,
                        }),
                    ),
                );
            }
            await onToggleSuccess?.(action);
        },
    });

    const shouldFetchCountOnly = Boolean(targetFollowedUserId) && typeof initialFollowerCount !== "number";

    const { data: followersCount } = useQuery({
        queryKey: ["agent-followers-count", targetFollowedUserIdStr],
        queryFn: () => getFollowersCount(String(targetFollowedUserIdStr)),
        enabled: shouldFetchCountOnly,
        retry: false,
    });

    React.useEffect(() => {
        if (mutation.isPending) return;
        if (typeof followersCount === "number" && Number.isFinite(followersCount)) {
            setFollowerCount(followersCount);
        }
    }, [followersCount, mutation.isPending]);

    const toggleFollow = ({ onError }: ToggleFollowOptions = {}) => {
        if (!isAuthenticated) {
            onRequireAuth();
            return;
        }
        if (!followerUserId || !targetFollowedUserIdStr) {
            onMissingIds?.();
            return;
        }

        const nextIsFollowing = !isFollowing;
        const action: FollowAction = nextIsFollowing ? "follow" : "unfollow";
        setIsFollowing(nextIsFollowing);
        setFollowerCount((prev) => Math.max(0, prev + (nextIsFollowing ? 1 : -1)));

        mutation.mutate(action, {
            onError: (error) => {
                setIsFollowing(!nextIsFollowing);
                setFollowerCount((prev) => Math.max(0, prev + (nextIsFollowing ? -1 : 1)));
                if (onError) {
                    onError(error);
                    return;
                }
                const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
                if (status === 429) {
                    toast.error("Too many follow actions. Please wait a moment and try again.", {
                        id: "FOLLOW_RATE_LIMIT",
                    });
                    return;
                }
                toast.error("Could not update follow right now. Please try again.", {
                    id: "FOLLOW_UNAVAILABLE",
                });
            },
        });
    };

    return {
        isFollowing,
        followerCount,
        isPending: mutation.isPending,
        toggleFollow,
    };
}
